import React, { useState } from 'react';
import VersionChip from './VersionChip';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';
import WriteButton from './WriteButton';
import CheckIcon from '@mui/icons-material/Check';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import GavelIcon from '@mui/icons-material/Gavel';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';

import { colors, primaryButtonSx, secondaryButtonSx, typo } from '../../theme/tokens';
import { mapStepStatus, mapWorkflowStatus, REPROMOTABLE_STEP_STATUSES } from '../../utils/promotionStatus';
import { timeAgo, elapsedSince, isLikelyStuck } from '../../utils/timeAgo';
import type { EmbeddedStep } from '../../api/promotionWorkflowApi';
import type { PipelineStepStatus } from './PipelineTimeline';

import StatusChip from './StatusChip';

export interface WorkflowSummary {
  workflow_uuid: string;
  /** Numeric id rendered as "W#42". */
  id: number;
  tenant_code: string | null;
  /** Server workflow status — in_progress | completed | failed | cancelled | rolled_back | superseded. */
  status: string;
  /** Created-by email rendered in the meta line. */
  created_by_email?: string;
  /** "Time ago" string (e.g. "12m ago"). Pre-formatted by caller. */
  createdAgo?: string;
  /** Home env — rendered as the track's source node. */
  home_env?: string;
  /** Semver this workflow promotes. */
  versionStr?: string | null;
  notes?: string | null;
}

export interface WorkflowCardProps {
  workflow: WorkflowSummary;
  /** RAW embedded step rows from the list endpoint (server statuses). */
  steps: EmbeddedStep[];
  /** Per-env deploy CTA — DIRECT topology, the user picks the target. */
  onPromote?: (workflowUuid: string, targetEnv: string) => void;
  /** A step is parked for approval — open the review surface (detail page). */
  onReview?: (workflowUuid: string) => void;
  onCancel?: (workflowUuid: string) => void;
  onViewDetail: (workflowUuid: string) => void;
  /** When true, render a brief attention ring (brand boxShadow). */
  highlight?: boolean;
}

/**
 * Left-edge accent — derived from the STEPS (the workflow status alone can't
 * say "needs approval"/"failed step": parked/failed live at step grain).
 */
const accentFor = (steps: EmbeddedStep[]): string | null => {
  let accent: string | null = null;
  for (const s of steps) {
    const v = mapStepStatus(s.status);
    if (v === 'awaiting_approval') return colors.approvalIcon; // strongest call to action
    if (v === 'failed') accent = colors.errorIcon;
    else if (v === 'partial' && !accent) accent = colors.warningIcon;
  }
  return accent;
};

const DOT = 22;

interface NodeVisual {
  bg: string;
  border?: string;
  icon: React.ReactNode | null;
  reached: boolean;
  pulse?: boolean;
}

const nodeVisual = (status: PipelineStepStatus | 'source'): NodeVisual => {
  if (status === 'source') {
    return {
      bg: colors.bgWhite,
      border: `2px solid ${colors.borderHover}`,
      icon: <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: colors.textTertiary }} />,
      reached: true,
    };
  }
  const filled = (node: React.ReactNode, bg: string, pulse = false): NodeVisual => ({
    bg,
    icon: node,
    reached: true,
    pulse,
  });
  switch (status) {
    case 'completed':
      return filled(<CheckIcon sx={{ fontSize: 14, color: '#fff' }} />, colors.successIcon);
    case 'in_flight':
      return filled(
        <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#fff' }} />,
        colors.brand,
        true,
      );
    case 'awaiting_approval':
      // Parked, not moving: distinct purple, deliberately NO pulse.
      return filled(<GavelIcon sx={{ fontSize: 12, color: '#fff' }} />, colors.approvalIcon);
    case 'partial':
      return filled(<WarningAmberIcon sx={{ fontSize: 13, color: '#fff' }} />, colors.warningIcon);
    case 'failed':
      return filled(<PriorityHighIcon sx={{ fontSize: 14, color: '#fff' }} />, colors.errorIcon);
    case 'expired':
      return filled(<AccessTimeIcon sx={{ fontSize: 13, color: '#fff' }} />, colors.textTertiary);
    case 'unknown':
      return filled(<HelpOutlineIcon sx={{ fontSize: 13, color: '#fff' }} />, colors.textTertiary);
    case 'pending':
    default:
      return { bg: colors.bgMuted, border: `1px solid ${colors.borderLight}`, icon: null, reached: false };
  }
};

/**
 * Hub-and-spoke env track: the source node, a divider, then INDEPENDENT target
 * nodes — deliberately no target→target connectors (DIRECT topology: qa and
 * uat are unordered peers; a chained line would imply a sequence that doesn't
 * exist).
 */
const EnvTrack: React.FC<{
  steps: EmbeddedStep[];
  homeEnv?: string;
  sublabelFor: (s: EmbeddedStep) => React.ReactNode;
  onClick: () => void;
}> = ({ steps, homeEnv, sublabelFor, onClick }) => {
  const home = (homeEnv ?? 'dev01').toUpperCase();
  const source = nodeVisual('source');
  return (
    <Box
      role="button"
      tabIndex={0}
      aria-label={`View workflow details — ${steps
        .map((s) => `${s.env.toUpperCase()}: ${mapStepStatus(s.status).replace(/_/g, ' ')}`)
        .join(', ')}`}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        flexWrap: 'wrap',
        gap: 2.5,
        cursor: 'pointer',
        borderRadius: '8px',
        '&:focus-visible': { outline: `2px solid ${colors.brand}`, outlineOffset: 2 },
      }}
    >
      {/* Source node */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, minWidth: 60 }}>
        <Box
          aria-label={`${home}: source`}
          sx={{
            width: DOT,
            height: DOT,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: source.bg,
            border: source.border,
          }}
        >
          {source.icon}
        </Box>
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.3px',
            fontFamily: 'Manrope, sans-serif',
            color: colors.textPrimary,
            lineHeight: 1.1,
          }}
        >
          ★ {home}
        </Typography>
        <Typography sx={{ fontSize: 10, color: colors.textTertiary, lineHeight: 1.1, minHeight: 12 }}>
          source
        </Typography>
      </Box>

      <Box aria-hidden sx={{ alignSelf: 'stretch', width: '1px', backgroundColor: colors.borderLight, my: 0.25 }} />

      {/* Independent target nodes */}
      {steps.map((step) => {
        const visualStatus = mapStepStatus(step.status);
        const v = nodeVisual(visualStatus);
        return (
          <Box
            key={`${step.env}-${step.step_order}`}
            sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, minWidth: 60 }}
          >
            <Box
              aria-label={`${step.env.toUpperCase()}: ${visualStatus.replace(/_/g, ' ')}`}
              sx={{
                width: DOT,
                height: DOT,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: v.bg,
                border: v.border,
                flexShrink: 0,
                ...(v.pulse && {
                  boxShadow: `0 0 0 4px ${colors.brandLight}`,
                  animation: 'wfCardPulse 2s infinite',
                  '@keyframes wfCardPulse': {
                    '0%': { boxShadow: `0 0 0 0 ${colors.brandLight}` },
                    '70%': { boxShadow: '0 0 0 7px rgba(250,179,6,0)' },
                    '100%': { boxShadow: '0 0 0 0 rgba(250,179,6,0)' },
                  },
                  '@media (prefers-reduced-motion: reduce)': {
                    animation: 'none',
                    boxShadow: `0 0 0 4px ${colors.brandLight}`,
                  },
                }),
              }}
            >
              {v.icon}
            </Box>
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '0.3px',
                fontFamily: 'Manrope, sans-serif',
                color: v.reached ? colors.textPrimary : colors.textTertiary,
                lineHeight: 1.1,
              }}
            >
              {step.env.toUpperCase()}
            </Typography>
            <Typography sx={{ fontSize: 10, color: colors.textTertiary, lineHeight: 1.1, minHeight: 12 }}>
              {sublabelFor(step) ?? ''}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

const dividerSx = { height: '1px', backgroundColor: colors.borderLight, my: 1.75 } as const;

/**
 * Workflow (version) card: header (id + version + status) · meta line ·
 * hub-and-spoke env track · per-env deploy actions. Cancel lives in the
 * overflow and mirrors the server predicate (nothing dispatched yet).
 */
/** Maps raw server sub_status values to user-friendly labels. */
const SUB_STATUS_LABEL: Record<string, string> = {
  sent: 'dispatching',
  verified: 'verifying',
  platform_written: 'writing platform',
  cache_invalidated: 'finishing',
};

const WorkflowCard: React.FC<WorkflowCardProps> = ({
  workflow,
  steps,
  onPromote,
  onReview,
  onCancel,
  onViewDetail,
  highlight,
}) => {
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [copied, setCopied] = useState(false);
  const accent = accentFor(steps);
  const wfChip = mapWorkflowStatus(workflow.status);

  const meta: string[] = [];
  if (workflow.createdAgo) meta.push(workflow.createdAgo);
  if (workflow.created_by_email) meta.push(`by ${workflow.created_by_email}`);

  // A superseded workflow is retired (a newer version exists): don't offer live
  // deploys from its History card — deploying its older version is an explicit
  // rollback, done from the Deploy Matrix, not here.
  const deployable =
    workflow.status === 'superseded'
      ? []
      : steps.filter((s) => REPROMOTABLE_STEP_STATUSES.has((s.status || '').toLowerCase()));
  // Ladder gating: this package's envs open strictly in step order (QA → UAT →
  // INTEG → PROD). Every env after the FIRST rung that hasn't reached a
  // terminal-success state (completed/partial) is locked behind it. The server
  // enforces the same ladder via env_policy prerequisites (fail-closed) — this
  // is the matching UX affordance.
  const lockedBehind = React.useMemo(() => {
    const done = new Set(['completed', 'partial']);
    const ordered = [...steps].sort((a, b) => (a.step_order ?? 0) - (b.step_order ?? 0));
    const locks = new Map<string, string>();
    let blocking: string | null = null;
    for (const s of ordered) {
      if (blocking) locks.set(s.env, blocking);
      else if (!done.has((s.status || '').toLowerCase())) blocking = s.env;
    }
    return locks;
  }, [steps]);
  const parked = steps.filter((s) => (s.status || '').toLowerCase() === 'awaiting_approval');
  // Cancel mirrors the server: workflow in_progress AND nothing dispatched
  // (every step pending-with-no-substatus or source-parked).
  const canCancel =
    workflow.status === 'in_progress' &&
    steps.every(
      (s) =>
        ((s.status || '').toLowerCase() === 'pending' && !s.sub_status) ||
        (s.status || '').toLowerCase() === 'awaiting_approval',
    );

  const sublabelFor = (s: EmbeddedStep): React.ReactNode => {
    const v = mapStepStatus(s.status);
    if (v === 'completed' && s.completed_at) return timeAgo(s.completed_at);
    if (v === 'awaiting_approval') return 'needs approval';
    if (v === 'in_flight') {
      const sinceIso = s.promoted_at ?? s.last_seen_status_at;
      const elapsed = elapsedSince(sinceIso);
      const subLabel = s.sub_status
        ? (SUB_STATUS_LABEL[s.sub_status] ?? 'applying')
        : (s.status || '').toLowerCase() === 'retrying'
        ? 'retrying'
        : 'applying';
      const text = elapsed ? `${subLabel} · ${elapsed}` : subLabel;
      const stuck = isLikelyStuck(sinceIso);
      if (stuck) {
        return (
          <Tooltip title="Taking longer than usual — the reconciler will keep retrying." arrow>
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
              {text}
              <WarningAmberIcon sx={{ fontSize: 11, color: colors.warningIcon, ml: 0.25 }} />
            </Box>
          </Tooltip>
        );
      }
      return text;
    }
    if (v === 'failed') return 'failed';
    if (v === 'expired') return 'expired';
    if (v === 'unknown') return 'unconfirmed';
    return undefined;
  };

  const copyLink = () => {
    void navigator.clipboard.writeText(
      `${window.location.origin}/tenants/${workflow.tenant_code ?? ''}/environment-promotion/${workflow.workflow_uuid}`,
    );
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Paper
      elevation={0}
      data-testid="workflow-card"
      data-uuid={workflow.workflow_uuid}
      sx={{
        p: 2.5,
        borderRadius: '12px',
        border: `1px solid ${colors.borderLight}`,
        backgroundColor: colors.bgWhite,
        borderLeft: accent ? `3px solid ${accent}` : `1px solid ${colors.borderLight}`,
        transition: 'box-shadow 0.3s, border-color 0.2s',
        ...(highlight && {
          boxShadow: `0 0 0 3px ${colors.brand}`,
          '@media (prefers-reduced-motion: reduce)': {
            boxShadow: `0 0 0 3px ${colors.brand}`,
          },
        }),
        '&:hover': {
          boxShadow: highlight
            ? `0 0 0 3px ${colors.brand}, 0 4px 12px rgba(0,0,0,0.05)`
            : '0 4px 12px rgba(0,0,0,0.05)',
          borderColor: colors.borderHover,
          ...(accent ? { borderLeftColor: accent } : {}),
        },
      }}
    >
      {/* Header: id + version (left), workflow status chip (right) */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
          <Typography sx={{ ...typo.mono, fontWeight: 700, fontSize: 14, color: colors.textPrimary }}>
            W#{workflow.id}
          </Typography>
          <Tooltip title={copied ? 'Copied!' : 'Copy workflow link'} arrow>
            <IconButton
              size="small"
              aria-label="Copy workflow link"
              onClick={copyLink}
              sx={{ p: 0.25 }}
            >
              <ContentCopyIcon sx={{ fontSize: 13, color: colors.textTertiary }} />
            </IconButton>
          </Tooltip>
          {workflow.versionStr && (
            <VersionChip version={workflow.versionStr} tooltip="Version this workflow deploys" />
          )}
        </Box>
        <StatusChip status={wfChip.chip} label={wfChip.label} />
      </Box>

      {/* Meta line: tenant · age · author */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.75, flexWrap: 'wrap', minWidth: 0 }}>
        {workflow.tenant_code && (
          <Typography
            title={workflow.tenant_code}
            sx={{
              ...typo.mono,
              fontSize: 12,
              color: colors.textSecondary,
              maxWidth: 280,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {workflow.tenant_code}
          </Typography>
        )}
        {workflow.tenant_code && meta.length > 0 && (
          <Box sx={{ width: 3, height: 3, borderRadius: '50%', backgroundColor: colors.borderDefault }} />
        )}
        {meta.map((m, i) => (
          <React.Fragment key={m}>
            {i > 0 && (
              <Box sx={{ width: 3, height: 3, borderRadius: '50%', backgroundColor: colors.borderDefault }} />
            )}
            <Typography sx={typo.tinyText}>{m}</Typography>
          </React.Fragment>
        ))}
      </Box>

      <Box sx={dividerSx} />

      <EnvTrack
        steps={steps}
        homeEnv={workflow.home_env}
        sublabelFor={sublabelFor}
        onClick={() => onViewDetail(workflow.workflow_uuid)}
      />

      <Box sx={dividerSx} />

      {/* Footer: per-env deploy CTAs + review + details (left), overflow (right) */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          {onPromote &&
            deployable.map((s) => {
              const redrive = (s.status || '').toLowerCase() !== 'pending';
              const lockedBy = lockedBehind.get(s.env);
              if (lockedBy) {
                return (
                  <Tooltip
                    key={s.env}
                    title={`Locked — complete ${lockedBy.toUpperCase()} first. Deployments ladder QA → UAT → INTEG → PROD.`}
                    arrow
                  >
                    <span>
                      <WriteButton
                        variant="contained"
                        size="small"
                        disabled
                        data-locked-by={lockedBy}
                        aria-label={`Deploy to ${s.env.toUpperCase()} (locked until ${lockedBy.toUpperCase()} completes)`}
                        sx={{ ...primaryButtonSx, px: 2, py: 0.625, fontSize: 13, borderRadius: '8px' }}
                      >
                        {`${s.env.toUpperCase()} 🔒`}
                      </WriteButton>
                    </span>
                  </Tooltip>
                );
              }
              return (
                <Tooltip
                  key={s.env}
                  title={redrive ? `Previous attempt ${s.status} — start a fresh deploy` : ''}
                  arrow
                >
                  <WriteButton
                    variant="contained"
                    size="small"
                    startIcon={<RocketLaunchIcon sx={{ fontSize: 16 }} />}
                    onClick={() => onPromote(workflow.workflow_uuid, s.env)}
                    // Explicit label — the wrapping Tooltip's title would
                    // otherwise become the accessible name.
                    aria-label={redrive ? `Retry ${s.env.toUpperCase()}` : `Deploy to ${s.env.toUpperCase()}`}
                    sx={{ ...primaryButtonSx, px: 2, py: 0.625, fontSize: 13, borderRadius: '8px' }}
                  >
                    {redrive ? `Retry ${s.env.toUpperCase()}` : `Deploy to ${s.env.toUpperCase()}`}
                  </WriteButton>
                </Tooltip>
              );
            })}
          {onReview && parked.length > 0 && (
            <WriteButton
              variant="outlined"
              size="small"
              startIcon={<GavelIcon sx={{ fontSize: 15 }} />}
              onClick={() => onReview(workflow.workflow_uuid)}
              sx={{
                ...secondaryButtonSx,
                px: 2,
                py: 0.55,
                fontSize: 13,
                borderRadius: '8px',
                color: colors.approvalIcon,
                borderColor: colors.approvalIcon,
                '&:hover': { borderColor: colors.approvalIcon, backgroundColor: colors.approvalBg },
              }}
            >
              Review approval
            </WriteButton>
          )}
          <Link
            component="button"
            onClick={() => onViewDetail(workflow.workflow_uuid)}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.25,
              fontSize: 13,
              fontWeight: 600,
              color: colors.textSecondary,
              textDecoration: 'none',
              fontFamily: 'Manrope, sans-serif',
              '&:hover': { color: colors.textPrimary, textDecoration: 'underline' },
            }}
          >
            Details
            <OpenInNewIcon sx={{ fontSize: 14 }} />
          </Link>
        </Box>
        <Box>
          <IconButton size="small" onClick={(e) => setMenuAnchor(e.currentTarget)} aria-label="Workflow actions">
            <MoreVertIcon sx={{ fontSize: 18 }} />
          </IconButton>
          <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
            <MenuItem
              onClick={() => {
                setMenuAnchor(null);
                onViewDetail(workflow.workflow_uuid);
              }}
              sx={{ fontSize: 13 }}
            >
              View detail
            </MenuItem>
            {onCancel && canCancel && (
              <MenuItem
                data-write-action="true"
                onClick={() => {
                  setMenuAnchor(null);
                  onCancel(workflow.workflow_uuid);
                }}
                sx={{ fontSize: 13, color: colors.errorText }}
              >
                Cancel workflow
              </MenuItem>
            )}
          </Menu>
        </Box>
      </Box>
    </Paper>
  );
};

export default WorkflowCard;
